import { useLocation } from 'react-router';
import { z } from 'zod';

import { isInternalPath } from './internal-path';

// Botón "volver" consciente del origen. La pantalla que enlaza a una de detalle
// (ficha, editor) pasa en el `state` de la navegación de dónde viene: ruta con su
// query (filtros incluidos) y una etiqueta legible. El destino lo lee y vuelve ahí;
// sin origen (deep-link, pestaña nueva) usa su padre jerárquico. No se usa
// `navigate(-1)`: con una pestaña nueva sacaría al usuario de la app.
//
// El origen del origen viaja anidado (editor ← ficha ← panel) para que al volver a
// la ficha su propio botón siga apuntando al panel. La cadena se acota para que
// navegar entre documentos relacionados no la haga crecer sin límite.

export const MAX_BACK_DEPTH = 4;

export interface BackTarget {
  to: string;
  label: string;
  state?: BackState;
  /** Entrada del historial del origen: al volver, la página recupera esa posición. */
  scrollKey?: string;
}

export interface BackState {
  back: BackTarget;
}

/** Regreso de las pantallas de primer nivel: su padre es el panel. */
export const TO_PANEL: BackTarget = { to: '/dashboard', label: 'Panel del QMS' };

export interface OriginLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key?: string;
}

const internalPath = z.string().max(2048).refine(isInternalPath);
// Las claves de historial de React Router son cortas; algo más largo no es una.
const historyKey = z.string().min(1).max(64);

const backShape = z.object({
  back: z.object({
    to: internalPath,
    label: z.string().trim().min(1).max(80),
    state: z.unknown().optional(),
    scrollKey: z.unknown().optional(),
  }),
});

/** Arma el destino; una clave de posición inválida se descarta sin perder el regreso. */
function target(to: string, label: string, rawKey: unknown, nested?: BackState): BackTarget {
  const key = historyKey.safeParse(rawKey);
  return {
    to,
    label,
    ...(nested ? { state: nested } : {}),
    ...(key.success ? { scrollKey: key.data } : {}),
  };
}

function sanitize(state: unknown, depth: number): BackState | undefined {
  if (depth <= 0) {
    return undefined;
  }
  const parsed = backShape.safeParse(state);
  if (!parsed.success) {
    return undefined;
  }
  const { to, label, scrollKey } = parsed.data.back;
  return { back: target(to, label, scrollKey, sanitize(parsed.data.back.state, depth - 1)) };
}

/** Origen válido guardado en el `state` de la navegación, o `null`. */
export function readBackTarget(state: unknown): BackTarget | null {
  return sanitize(state, MAX_BACK_DEPTH)?.back ?? null;
}

/** `state` para un enlace que sale de `origin` hacia una pantalla de detalle. */
export function backStateFrom(label: string, origin: OriginLocation): BackState {
  const to = `${origin.pathname}${origin.search}${origin.hash}`;
  return { back: target(to, label, origin.key, sanitize(origin.state, MAX_BACK_DEPTH - 1)) };
}

/** Hook del lado que enlaza: `<Link to=… state={useBackState('Panel del QMS')}>`. */
export function useBackState(label: string): BackState {
  return backStateFrom(label, useLocation());
}

/** Hook del lado destino: el origen recibido o el padre jerárquico. */
export function useBackTarget(fallback: BackTarget): BackTarget {
  return readBackTarget(useLocation().state) ?? fallback;
}
